import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { LEAD_STATUSES, DIMENSION_LABELS } from "./exterior-services";
import { PROPERTY_PHOTO_PATH, normalisePostcode } from "./quote-schema";

/*
 * Lead workflow: submitted → share_approved (customer consents to sharing details,
 * NOT a price) → partner_assigned (owner) → partner_priced (partner's proposed price)
 * → finalised (owner, only after partner confirmation) → customer_accepted/declined.
 * Nothing here books or charges anything.
 */

const tokenInput = z.object({ id: z.string().uuid(), claimToken: z.string().uuid() });

const CUSTOMER_FIELDS =
  "id, reference, services, property_type, postcode, town, created_at, status, workflow_stage, estimate_low_pence, estimate_high_pence, estimate_breakdown, estimate_needs_review, share_approved_at, assigned_at, partner_confirmed_at, final_price_pence, finalised_at, customer_accepted_at, customer_declined_at, visit_preferred_date, visit_time_window, visit_requested_at";

export const VISIT_WINDOWS = ["morning", "afternoon", "evening", "any"] as const;

/** Owner alert; skipped (never guessed) when no owner recipient is configured. */
async function notifyOwner(key: string, subject: string, body: string, details: { label: string; value: string }[]) {
  const ownerEmail = process.env["SHIELDS_LEADS_OWNER_EMAIL"];
  if (!ownerEmail) return false;
  try {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    await sendTemplateEmail("notification", ownerEmail, {
      idempotencyKey: key,
      templateData: { subject, title: subject, body, details, ctaText: "Open lead inbox", ctaUrl: `${(process.env["PUBLIC_APP_URL"] || "https://groundandglass.com").replace(/\/$/, "")}/owner/leads` },
    });
    return true;
  } catch { console.warn("Owner notification could not be sent."); return false; }
}

async function admin() {
  return (await import("@/integrations/supabase/client.server")).supabaseAdmin;
}

/** Customer view of their request, authorised by the private link token (no account needed). */
export const getQuoteStatus = createServerFn({ method: "POST" })
  .inputValidator((d) => tokenInput.parse(d))
  .handler(async ({ data }) => {
    const db = await admin();
    const { data: row, error } = await db.from("quote_requests").select(CUSTOMER_FIELDS).eq("id", data.id).eq("claim_token", data.claimToken).maybeSingle();
    if (error) throw new Error("Your quote status is temporarily unavailable. Please retry later.");
    if (!row) return null;
    // The final price is only revealed once Shields has finalised it.
    return { ...row, final_price_pence: row.finalised_at ? row.final_price_pence : null };
  });

export const approveSharing = createServerFn({ method: "POST" })
  .inputValidator((d) => tokenInput.parse(d))
  .handler(async ({ data }) => {
    const db = await admin();
    const { data: row, error } = await db.from("quote_requests")
      .update({ share_approved_at: new Date().toISOString(), workflow_stage: "share_approved" })
      .eq("id", data.id).eq("claim_token", data.claimToken).eq("workflow_stage", "submitted").select("id").maybeSingle();
    return { ok: !error && Boolean(row) };
  });

export const respondToFinalPrice = createServerFn({ method: "POST" })
  .inputValidator((d) => tokenInput.extend({ accept: z.boolean() }).parse(d))
  .handler(async ({ data }) => {
    const db = await admin();
    const now = new Date().toISOString();
    const { data: row, error } = await db.from("quote_requests")
      .update(data.accept
        ? { customer_accepted_at: now, workflow_stage: "customer_accepted", status: "won", status_changed_at: now }
        : { customer_declined_at: now, workflow_stage: "customer_declined", status: "lost", status_changed_at: now })
      .eq("id", data.id).eq("claim_token", data.claimToken).eq("workflow_stage", "finalised").not("finalised_at", "is", null)
      .select("id").maybeSingle();
    return { ok: !error && Boolean(row) };
  });

/** Customer asks for a visit after seeing their estimate. A request, not a confirmed booking. */
export const requestVisit = createServerFn({ method: "POST" })
  .inputValidator((d) => tokenInput.extend({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    window: z.enum(VISIT_WINDOWS),
    notes: z.string().trim().max(500).optional().default(""),
  }).parse(d))
  .handler(async ({ data }) => {
    const day = new Date(`${data.date}T12:00:00Z`).getTime(), today = Date.now() - 86_400_000;
    if (!(day > today) || day > Date.now() + 120 * 86_400_000) return { ok: false };
    const db = await admin();
    const { data: row, error } = await db.from("quote_requests")
      .update({ visit_preferred_date: data.date, visit_time_window: data.window, visit_notes: data.notes || null, visit_requested_at: new Date().toISOString() })
      .eq("id", data.id).eq("claim_token", data.claimToken).not("workflow_stage", "in", "(customer_declined)")
      .select("id, reference, postcode").maybeSingle();
    if (error || !row) return { ok: false };
    await notifyOwner(`visit-request-${row.id}-${data.date}-${data.window}`, `Visit requested · ${row.reference}`, "A homeowner has asked for a visit. Confirm a time with them before treating it as booked.", [{ label: "Reference", value: row.reference }, { label: "Area", value: row.postcode ?? "" }, { label: "Preferred date", value: data.date }, { label: "Time", value: data.window }]);
    return { ok: true };
  });

/* ---------------- Owner ---------------- */

async function assertOwner(supabase: { rpc: (...a: never[]) => unknown }, userId: string) {
  const { data } = (await (supabase.rpc as unknown as (f: string, a: object) => Promise<{ data: boolean | null }>)("has_role", { _user_id: userId, _role: "owner" }));
  if (!data) throw Object.assign(new Error("Owner access required"), { statusCode: 403 });
}

export const listLeads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertOwner(context.supabase as never, context.userId);
    const { data, error } = await context.supabase.from("quote_requests")
      .select("id, reference, services, property_type, postcode, town, full_name, status, workflow_stage, created_at, status_changed_at, estimate_low_pence, estimate_high_pence, estimate_needs_review, assigned_partner_id, partner_price_pence, final_price_pence, share_approved_at, assigned_at, partner_confirmed_at, finalised_at, customer_accepted_at, customer_declined_at, photo_paths")
      .order("created_at", { ascending: false }).limit(500);
    if (error) throw new Error(error.message);
    const { data: partners, error: partnerError } = await context.supabase.from("partner_profiles").select("user_id, trading_name, services, base_postcode").eq("published", true);
    if (partnerError) throw new Error("Could not load eligible partners.");
    return { leads: data ?? [], partners: partners ?? [] };
  });

export const getLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertOwner(context.supabase as never, context.userId);
    const { data: lead, error } = await context.supabase.from("quote_requests").select("*").eq("id", data.id).maybeSingle();
    if (error || !lead) throw new Error("Lead not found");
    const { data: internal } = await context.supabase.from("quote_request_internal").select("*").eq("request_id", data.id).maybeSingle();
    const photos = await signLeadPhotos(lead.photo_paths ?? []);
    const { claim_token: _t, ip_hash: _h, ...safe } = lead;
    return { lead: safe, internal, photos };
  });

async function signLeadPhotos(paths: string[]) {
  const valid = paths.filter((p) => /^pending\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.[a-z]+$/.test(p) || PROPERTY_PHOTO_PATH.test(p));
  if (!valid.length) return [];
  const db = await admin();
  const { data } = await db.storage.from("lead-photos").createSignedUrls(valid, 3600);
  return (data ?? []).map((d) => d.signedUrl).filter(Boolean) as string[];
}

export const updateLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    id: z.string().uuid(),
    status: z.enum(LEAD_STATUSES).optional(),
    internalNotes: z.string().max(5000).optional(),
    assignedProvider: z.string().max(200).optional(),
    assignedPartnerId: z.string().uuid().nullable().optional(),
  }).parse(d))
  .handler(async ({ data, context }) => {
    await assertOwner(context.supabase as never, context.userId);
    const now = new Date().toISOString();
    const patch: Record<string, unknown> = {};
    if (data.status) Object.assign(patch, { status: data.status, status_changed_at: now });
    if (data.assignedPartnerId !== undefined) {
      if (data.assignedPartnerId) {
        const { data: request } = await context.supabase.from("quote_requests").select("services").eq("id", data.id).single();
        const { data: partner } = await context.supabase.from("partner_profiles").select("user_id, services").eq("user_id", data.assignedPartnerId).maybeSingle();
        if (!partner || !request?.services.some(service => partner.services.includes(service))) return { ok: false as const, error: "Choose a partner who lists a matching service." };
      }
      const { data: cur } = await context.supabase.from("quote_requests").select("share_approved_at, workflow_stage").eq("id", data.id).single();
      if (data.assignedPartnerId && !cur?.share_approved_at) return { ok: false as const, error: "The customer hasn't approved sharing their details with a partner yet." };
      if (cur && ["finalised", "customer_accepted", "customer_declined"].includes(cur.workflow_stage)) return { ok: false as const, error: "This quote is already finalised." };
      Object.assign(patch, data.assignedPartnerId
        ? { assigned_partner_id: data.assignedPartnerId, assigned_at: now, workflow_stage: "partner_assigned", partner_price_pence: null, partner_confirmed_at: null, partner_price_note: null }
        : { assigned_partner_id: null, assigned_at: null, workflow_stage: "share_approved", partner_price_pence: null, partner_confirmed_at: null, partner_price_note: null });
    }
    if (Object.keys(patch).length) {
      const { error } = await context.supabase.from("quote_requests").update(patch as never).eq("id", data.id);
      if (error) return { ok: false as const, error: error.message };
    }
    if (data.internalNotes !== undefined || data.assignedProvider !== undefined) {
      const { error } = await context.supabase.from("quote_request_internal").upsert({
        request_id: data.id,
        ...(data.internalNotes !== undefined ? { internal_notes: data.internalNotes } : {}),
        ...(data.assignedProvider !== undefined ? { assigned_provider: data.assignedProvider } : {}),
        updated_by: context.userId,
        updated_at: now,
      });
      if (error) return { ok: false as const, error: error.message };
    }
    return { ok: true as const };
  });

/** Owner finalises the quote — only possible once the assigned partner has confirmed a price. */
export const finaliseQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid(), finalPricePence: z.number().int().min(100).max(10_000_000) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertOwner(context.supabase as never, context.userId);
    const now = new Date().toISOString();
    const { data: row, error } = await context.supabase.from("quote_requests")
      .update({ final_price_pence: data.finalPricePence, finalised_at: now, workflow_stage: "finalised", status: "quoted", status_changed_at: now })
      .eq("id", data.id).eq("workflow_stage", "partner_priced").not("partner_confirmed_at", "is", null)
      .select("id").maybeSingle();
    if (error || !row) return { ok: false as const, error: "A final price can only be set after the assigned partner has confirmed their price." };
    return { ok: true as const };
  });

/* ---------------- Partner ---------------- */

const PARTNER_FIELDS = "id, reference, services, property_type, town, postcode, dimensions, description, photo_paths, workflow_stage, estimate_low_pence, estimate_high_pence, assigned_at, partner_price_pence, partner_price_note, partner_confirmed_at, finalised_at, final_price_pence";

export const listAssignedLeads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const db = await admin();
    const { data, error } = await db.from("quote_requests").select(PARTNER_FIELDS).eq("assigned_partner_id", context.userId).order("assigned_at", { ascending: false });
    if (error) throw new Error("Assigned enquiries are temporarily unavailable.");
    return Promise.all((data ?? []).map(async (r) => ({ ...r, photos: await signLeadPhotos(r.photo_paths ?? []) })));
  });

export const submitPartnerPrice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid(), pricePence: z.number().int().min(100).max(10_000_000), note: z.string().trim().max(1000).optional().default("") }).parse(d))
  .handler(async ({ data, context }) => {
    const db = await admin();
    const { data: row, error } = await db.from("quote_requests")
      .update({ partner_price_pence: data.pricePence, partner_price_note: data.note || null, partner_confirmed_at: new Date().toISOString(), workflow_stage: "partner_priced" })
      .eq("id", data.id).eq("assigned_partner_id", context.userId).in("workflow_stage", ["partner_assigned", "partner_priced"])
      .select("id, reference").maybeSingle();
    if (error || !row) return { ok: false };
    const emailed = await notifyOwner(`partner-price-${row.id}-${data.pricePence}`, `Partner quote received · ${row.reference}`, "A partner has submitted a price. Review and finalise it in your lead inbox.", [{ label: "Reference", value: row.reference }, { label: "Partner price", value: `£${(data.pricePence / 100).toFixed(2)}` }, ...(data.note ? [{ label: "Note", value: data.note }] : [])]);
    return { ok: true, emailed };
  });

/* ---------------- Optional customer account ---------------- */

export const getMyProperty = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: profile, error: profileError } = await context.supabase.from("customer_property_profiles").select("*").eq("user_id", context.userId).maybeSingle();
    const { data: requests, error: requestsError } = await context.supabase.from("quote_requests").select(CUSTOMER_FIELDS).eq("user_id", context.userId).order("created_at", { ascending: false });
    if (profileError || requestsError) throw new Error("Your saved property is temporarily unavailable. Please retry later.");
    return { profile, requests: (requests ?? []).map(row => ({ ...row, final_price_pence: row.finalised_at ? row.final_price_pence : null })) };
  });

export const saveMyProperty = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    fullName: z.string().trim().max(100).optional().default(""),
    phone: z.string().trim().max(25).optional().default(""),
    addressLine: z.string().trim().max(200).optional().default(""),
    town: z.string().trim().max(100).optional().default(""),
    postcode: z.string().trim().max(10).refine(value => !value || /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value), "Enter a valid postcode").optional().default(""),
    propertyType: z.enum(["house", "flat", "commercial", "other"]).nullable().optional(),
    dimensions: z.record(z.string().refine(key => key in DIMENSION_LABELS), z.number().min(0).max(100000)).optional().default({}),
    photoPaths: z.array(z.string().regex(PROPERTY_PHOTO_PATH)).max(8).optional(),
  }).parse(d))
  .handler(async ({ data, context }) => {
    if (data.photoPaths?.some(path => !path.startsWith(`users/${context.userId}/`))) throw new Error("These photos do not belong to your account.");
    const { error } = await context.supabase.from("customer_property_profiles").upsert({
      user_id: context.userId,
      full_name: data.fullName || null,
      phone: data.phone || null,
      address_line: data.addressLine || null,
      town: data.town || null,
      postcode: data.postcode ? normalisePostcode(data.postcode) : null,
      property_type: data.propertyType ?? null,
      dimensions: data.dimensions,
      ...(data.photoPaths ? { photo_paths: data.photoPaths } : {}),
      updated_at: new Date().toISOString(),
    });
    return { ok: !error, error: error?.message };
  });
